import {
  useGetDocumentFoldersQuery, // Assuming this is the type for the document prop
  useGetDocumentHouseholdPermissionsQuery,
  useUpdateDocumentHouseholdPermissionsMutation,
  useUpdateDocumentMutation,
  useUploadDocumentMutation,
  type DocumentFolderRecord,
  type DocumentHouseholdMember,
  type DocumentHouseholdPermission,
  type DocumentHouseholdPermissionRecord,
  type DocumentPermission,
  type DocumentRecord, // Assuming this is the type for the document prop
  type NetworkedDocumentRecord,
} from '@/store/documentsApi';
import { getExtensionFromFilename } from '@/utils/fileValidation';
import {
  skipViewingInvestorTagForAccountsQuery,
  useGetAccountsQuery,
} from '@/store/accountsApi';
import type { AccountBase } from '@/models/AccountViewModel';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { mobileWidth } from '@/constants';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { WARootState } from '@/store/store';
import { useLocalization } from '@/hooks/useLocalization';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useGlobalSnackbar } from '@/contexts/snackContext';
import {
  filterFoldersForMove,
  getServerMessage,
  DUPLICATE_FOLDER_SERVER_MESSAGE,
} from '@/modules/document/grids/documentVault/documentVaultGridUtils';

export interface PendingUpload {
  files: File[];
  folderId?: string;
}

interface EditDocumentModalProps {
  open: boolean;
  onClose: () => void;
  document: DocumentRecord | null; // Document to edit
  documents?: DocumentRecord[];
  investorId?: number;
  pendingUpload?: PendingUpload;
}

interface FolderOption {
  id: number | null; // null for root
  name: string; // Full path name
  level: number;
}

interface AccountOption {
  id: number;
  name: string;
}

interface DocumentDraft {
  name: string;
  selectedFolderId: number | null;
  selectedAccounts: AccountOption[];
  permissions: DocumentPermission[];
  catalogAccountIds: number[];
  accountsEdited: boolean;
}

// Helper function to flatten the folder structure and create paths
const flattenFolders = (
  folders: DocumentFolderRecord[],
  rootLabel: string,
  path = '',
  level = 1,
): FolderOption[] => {
  let options: FolderOption[] = [];
  folders.forEach((folder) => {
    // Use '/' as separator, trim trailing slash if path exists
    const folderName = folder.VaultFileId == null ? rootLabel : folder.Name;
    const currentPath = path ? `${path} / ${folderName}` : folderName;
    options.push({
      id: folder.VaultFileId ?? null, // Root might not have VaultFileId in some APIs
      name: currentPath,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Level is typed required but may be omitted by the API; fall back to the computed depth so indentation can't become NaN.
      level: folder.Level ?? level,
    });
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Children is typed as a required array, but the folders API can omit it for leaf nodes; guard so `.length` can't throw.
    if (folder.Children && folder.Children.length > 0) {
      options = options.concat(
        flattenFolders(folder.Children, rootLabel, currentPath, level + 1),
      );
    }
  });
  return options;
};

const PENDING_ID_PREFIX = 'pending-';

const buildPendingDocument = (
  file: File,
  index: number,
  folderId?: string,
): DocumentRecord => ({
  id: -(index + 1),
  VaultFileId: `${PENDING_ID_PREFIX}${String(index)}`,
  Name: file.name,
  Extension: getExtensionFromFilename(file.name) ?? '',
  IsFolder: false,
  CanMove: true,
  CanRename: true,
  CanDelete: false,
  CanSign: false,
  IsSignatureRequested: false,
  IsSigned: false,
  FileSize: file.size,
  NumberOfFiles: 0,
  NumberOfFolders: 0,
  UploadDate: '',
  ParentId: folderId ? Number(folderId) : null,
  AccountId: null,
  Associations: [],
  Permissions: [],
});

const formatHouseholdMembers = (
  members: DocumentHouseholdMember[],
  and: string,
): string => {
  const names = members.map((member) =>
    member.UserName ? `${member.Name} (${member.UserName})` : member.Name,
  );

  if (names.length <= 1) return names.join('');
  if (names.length === 2) return `${names[0]} ${and} ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, ${and} ${names[names.length - 1]}`;
};

const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  open,
  onClose,
  document,
  documents,
  investorId,
  pendingUpload,
}) => {
  const { t } = useLocalization();
  const { showErrorSnackbar, showSuccessSnackbar } = useGlobalSnackbar();
  const isMobile = useMediaQuery(mobileWidth);
  const [isFolderSelectorOpen, setIsFolderSelectorOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Keyed by the document's (string) VaultFileId. Values are optional because a
  // draft is built lazily, so a lookup can miss until buildDraft runs.
  const [drafts, setDrafts] = useState<
    Record<string, DocumentDraft | undefined>
  >({});

  const [householdEdits, setHouseholdEdits] = useState<
    Record<string, DocumentHouseholdPermission[] | undefined>
  >({});
  const [householdSaveError, setHouseholdSaveError] = useState(false);
  // Keyed by the pending id. A file stays here once the server has it, so a
  // retry after a failed rename does not upload it a second time.
  const [uploadedRecords, setUploadedRecords] = useState<
    Record<string, NetworkedDocumentRecord | undefined>
  >({});
  const [savedUploads, setSavedUploads] = useState<Record<string, true>>({});
  const [isSavingUpload, setIsSavingUpload] = useState(false);
  const [nameError, setNameError] = useState<{
    vaultFileId: string;
    message: string;
  } | null>(null);
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );
  const isAdvisorRole = (currentRole || '').toLowerCase() === 'advisor';
  const { isImpersonating, roleImpersonatedBy } = useImpersonation();
  // Only the investor (the account owner) should load their accounts to
  // associate. Every other role (advisor, owner, banker, firm, estate, admin)
  // is viewing a client's vault, so the account dropdown stays excluded there.
  const isInvestorRole = (currentRole || '').toLowerCase() === 'investor';
  const folderQueryArgs = isAdvisorRole
    ? typeof investorId === 'number'
      ? { InvestorId: investorId }
      : undefined
    : undefined;

  const effectiveDocuments = useMemo(() => {
    if (pendingUpload) {
      return pendingUpload.files.map((file, index) =>
        buildPendingDocument(file, index, pendingUpload.folderId),
      );
    }
    if (documents && documents.length > 0) return documents;
    return document ? [document] : [];
  }, [document, documents, pendingUpload]);

  // Fetching data
  const { data: foldersData, isLoading: isLoadingFolders } =
    useGetDocumentFoldersQuery(folderQueryArgs, {
      // Investor mode calls /api/investor/DocumentFolders with no args.
      // Advisor mode requires an explicit investor id.
      skip: isAdvisorRole && typeof investorId !== 'number',
    });
  // An advisor editing a client's files loads that client's accounts; folders
  // have no accounts, so an advisor editing only folders skips the query.
  const isAdvisorEditingClientFile =
    isAdvisorRole &&
    typeof investorId === 'number' &&
    effectiveDocuments.some((doc) => !doc.IsFolder);
  const shouldLoadAccounts =
    open && (isInvestorRole || isAdvisorEditingClientFile);
  const {
    data: accountsData,
    isLoading: isAccountsQueryLoading,
    isError: isAccountsError,
  } = useGetAccountsQuery(
    {
      showClosed: false,
      ...skipViewingInvestorTagForAccountsQuery,
      ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
    },
    {
      skip: !shouldLoadAccounts,
    },
  );
  const isLoadingAccounts =
    isAccountsQueryLoading ||
    (shouldLoadAccounts && !accountsData && !isAccountsError);
  const [updateDocument, { isLoading: isUpdatingDocument }] =
    useUpdateDocumentMutation();
  const [uploadDocument] = useUploadDocumentMutation();
  const isUpdating = isUpdatingDocument || isSavingUpload;
  const [
    updateDocumentHouseholdPermissions,
    { isLoading: isUpdatingHouseholdPermissions },
  ] = useUpdateDocumentHouseholdPermissionsMutation();

  const canShareWithHousehold = isInvestorRole && investorId === undefined;

  const { data: householdData } = useGetDocumentHouseholdPermissionsQuery(
    undefined,
    {
      // The endpoint runs two stored procedures across the investor's whole
      // vault, so don't pay for it until the modal is actually open.
      skip: !open || !canShareWithHousehold,
    },
  );

  const isBulkEdit = effectiveDocuments.length > 1;
  const isLastDocument = currentIndex === effectiveDocuments.length - 1;

  // Index access is typed non-undefined (noUncheckedIndexedAccess is off) but can
  // be undefined at runtime when the list is empty / index is out of range, so
  // assert the honest type to keep the null guards below meaningful.
  const currentDocument =
    (effectiveDocuments[currentIndex] as DocumentRecord | undefined) ?? null;
  const rootLabel = t('personalStorage');

  // Memoize folder options
  const folderOptions = useMemo(() => {
    if (!foldersData?.Records) return [];
    // An advisor can't move a folder into itself or one of its subfolders.
    const blockedIds = new Set(
      isAdvisorRole && currentDocument?.IsFolder
        ? [Number(currentDocument.VaultFileId)]
        : [],
    );
    const records = filterFoldersForMove(foldersData.Records, blockedIds);
    const flattened = flattenFolders(records, rootLabel);
    // An empty tree still offers the root as a destination.
    return flattened.length > 0
      ? flattened
      : [{ id: null, name: rootLabel, level: 0 }];
  }, [foldersData, isAdvisorRole, currentDocument, rootLabel]);

  const accountOptions = useMemo((): AccountOption[] => {
    const records: AccountBase[] | undefined = accountsData?.Records;
    if (!records) return [];
    return records
      .map((account) => ({
        id: account.AccountId,
        name: account.Name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [accountsData]);

  const defaultFolder = useMemo(
    () =>
      folderOptions.find((f) => f.id === null) ?? {
        id: null,
        name: rootLabel,
        level: 0,
      },
    [folderOptions, rootLabel],
  );

  const buildDraft = React.useCallback(
    (doc: DocumentRecord): DocumentDraft => {
      const initialAccounts: AccountOption[] = [];
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Associations is typed as a required array but may be omitted by the API; guard before iterating.
      if (doc.Associations && accountOptions.length > 0) {
        doc.Associations.forEach((assoc) => {
          const matchingAccount = accountOptions.find(
            (opt) =>
              assoc.WealthAccountId != null && opt.id === assoc.WealthAccountId,
          );
          if (
            matchingAccount &&
            !initialAccounts.some((acc) => acc.id === matchingAccount.id)
          ) {
            initialAccounts.push(matchingAccount);
          }
        });
      }

      return {
        name: doc.Name || '',
        selectedFolderId: doc.ParentId ?? null,
        selectedAccounts: initialAccounts,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Permissions is typed as a required array but may be omitted by the API; fall back so the render `.map` can't throw.
        permissions: doc.Permissions || [],
        catalogAccountIds: accountOptions.map((account) => account.id),
        accountsEdited: false,
      };
    },
    [accountOptions],
  );

  useEffect(() => {
    if (!open) {
      setCurrentIndex(0);
      setDrafts({});
      setHouseholdEdits({});
      setHouseholdSaveError(false);
      setNameError(null);
      setIsFolderSelectorOpen(false);
      setUploadedRecords({});
      setSavedUploads({});
      return;
    }

    setUploadedRecords({});
    setSavedUploads({});
    setCurrentIndex(0);
    setIsFolderSelectorOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    // Build drafts only after the accounts query settles for investors. A draft
    // built while accountOptions is still empty caches empty selectedAccounts
    // under `prev[...] ??` and never picks up associations when accounts arrive.
    if (isLoadingAccounts) {
      return;
    }

    setDrafts((prev) => {
      const next: Record<string, DocumentDraft | undefined> = {};
      effectiveDocuments.forEach((doc) => {
        const existing = prev[doc.VaultFileId];
        const rebuilt = buildDraft(doc);
        if (!existing) {
          next[doc.VaultFileId] = rebuilt;
          return;
        }
        if (existing.accountsEdited) {
          next[doc.VaultFileId] = existing;
          return;
        }
        next[doc.VaultFileId] = {
          ...existing,
          selectedAccounts: rebuilt.selectedAccounts,
          catalogAccountIds: rebuilt.catalogAccountIds,
        };
      });
      return next;
    });
  }, [buildDraft, effectiveDocuments, open, isLoadingAccounts]);

  const currentDraft = currentDocument
    ? (drafts[currentDocument.VaultFileId] ?? buildDraft(currentDocument))
    : null;

  const impersonatorRole = (roleImpersonatedBy ?? '').trim().toLowerCase();

  const isAdvisorViewer =
    isAdvisorRole ||
    impersonatorRole === 'advisor' ||
    (isImpersonating && impersonatorRole === '');

  const showFinancialTeamPermissions = !isAdvisorViewer;

  const householdRecordsByVaultFileId = useMemo(() => {
    const map = new Map<string, DocumentHouseholdPermissionRecord>();
    // The backend types VaultFileId as int while the documents API hands it
    // back as a string; normalize to string so the two can be matched.
    householdData?.Records.forEach((record) => {
      map.set(String(record.VaultFileId), record);
    });
    return map;
  }, [householdData]);

  const currentHouseholdRows: DocumentHouseholdPermission[] = currentDocument
    ? (householdEdits[currentDocument.VaultFileId] ??
      householdRecordsByVaultFileId.get(currentDocument.VaultFileId)
        ?.Permissions ??
      [])
    : [];

  // Legacy hides the whole block when the document has no household rows:
  // GetForDocument returns null and `renderPermissions` bails out. (EUD-434)
  const showHouseholdSharing =
    canShareWithHousehold && currentHouseholdRows.length > 0;

  const householdConjunction = t('common.and', 'and');

  const selectedFolder = useMemo(() => {
    if (!currentDraft) return defaultFolder;
    return (
      folderOptions.find((opt) => opt.id === currentDraft.selectedFolderId) ??
      defaultFolder
    );
  }, [currentDraft, defaultFolder, folderOptions]);

  const updateCurrentDraft = React.useCallback(
    (updater: (draft: DocumentDraft) => DocumentDraft) => {
      if (!currentDocument) return;
      setDrafts((prev) => {
        const existing =
          prev[currentDocument.VaultFileId] ?? buildDraft(currentDocument);
        return {
          ...prev,
          [currentDocument.VaultFileId]: updater(existing),
        };
      });
    },
    [buildDraft, currentDocument],
  );

  const handlePermissionChange = (advisorId: number, checked: boolean) => {
    updateCurrentDraft((draft) => ({
      ...draft,
      permissions: draft.permissions.map((p) =>
        p.AdvisorId === advisorId ? { ...p, AllowedToSee: checked } : p,
      ),
    }));
  };

  const handleHouseholdPermissionChange = (
    investorHHId: number,
    checked: boolean,
  ) => {
    if (!currentDocument) return;
    const vaultFileId = currentDocument.VaultFileId;

    setHouseholdEdits((prev) => {
      const base =
        prev[vaultFileId] ??
        householdRecordsByVaultFileId.get(vaultFileId)?.Permissions ??
        [];

      return {
        ...prev,
        [vaultFileId]: base.map((row) =>
          row.InvestorHHId === investorHHId ? { ...row, CanSee: checked } : row,
        ),
      };
    });
  };

  const currentNameError =
    nameError && nameError.vaultFileId === currentDocument?.VaultFileId
      ? nameError.message
      : undefined;

  const showDuplicateNameError = (vaultFileId: string) => {
    setNameError({
      vaultFileId,
      message: t('documentVault.folderNameExists'),
    });
    const index = effectiveDocuments.findIndex(
      (doc) => doc.VaultFileId === vaultFileId,
    );
    if (index >= 0) setCurrentIndex(index);
  };

  const buildUpdatePayload = (
    doc: NetworkedDocumentRecord,
    draft: DocumentDraft,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Associations is typed as a required array but may be omitted by the API; fall back before iterating.
    const existingAssociations = doc.Associations || [];
    // A folder has no accounts field, so it keeps its associations as listed.
    const updatedAssociations: DocumentRecord['Associations'] = doc.IsFolder
      ? [...existingAssociations]
      : draft.selectedAccounts.map((acc) => {
          const existing = existingAssociations.find(
            (assoc) => assoc.WealthAccountId === acc.id,
          );
          const groupingId = existing?.WealthId;
          return {
            WealthAccountId: acc.id,
            ...(groupingId != null ? { WealthId: groupingId } : {}),
            VaultFileId: doc.VaultFileId,
          };
        });
    const openAccountIds = new Set(draft.catalogAccountIds);
    const associationsToMerge = doc.IsFolder ? [] : existingAssociations;
    associationsToMerge.forEach((assoc) => {
      const groupingId = assoc.WealthId;
      const accountId = assoc.WealthAccountId;
      const isGroupingOnly = groupingId != null && accountId == null;

      if (isGroupingOnly) {
        const alreadyKept = updatedAssociations.some(
          (row) => row.WealthAccountId == null && row.WealthId === groupingId,
        );
        if (!alreadyKept) {
          updatedAssociations.push({
            WealthId: groupingId,
            VaultFileId: doc.VaultFileId,
          });
        }
        return;
      }

      if (accountId == null) return;
      if (openAccountIds.has(accountId)) return;

      const alreadyKept = updatedAssociations.some(
        (row) => row.WealthAccountId === accountId,
      );
      if (!alreadyKept) {
        updatedAssociations.push({
          WealthAccountId: accountId,
          ...(groupingId != null ? { WealthId: groupingId } : {}),
          VaultFileId: doc.VaultFileId,
        });
      }
    });

    return {
      ...doc,
      Name: draft.name.trim(),
      ParentId: draft.selectedFolderId,
      // The server rejects every sharing row from an advisor, unchanged or not.
      Permissions: isAdvisorViewer ? [] : draft.permissions,
      Associations: updatedAssociations,
      InvestorId: investorId,
      VaultFileId: doc.VaultFileId,
      AccountId: doc.AccountId ?? null,
      // Defensive defaults for the PUT payload: these fields are typed as
      // required, but if the documents API omits any we send a valid default
      // instead of `undefined`. Intentional — do not "simplify" the `??`.
      /* eslint-disable @typescript-eslint/no-unnecessary-condition */
      CanSign: doc.CanSign ?? false,
      IsSignatureRequested: doc.IsSignatureRequested ?? false,
      IsSigned: doc.IsSigned ?? false,
      CanDelete: doc.CanDelete ?? false,
      CanMove: doc.CanMove ?? false,
      CanRename: doc.CanRename ?? true,
      Extension: doc.Extension ?? '',
      FileSize: doc.FileSize ?? 0,
      IsFolder: doc.IsFolder ?? false,
      UploadDate: doc.UploadDate ?? new Date().toISOString(),
      /* eslint-enable @typescript-eslint/no-unnecessary-condition */
    };
  };

  const handleSaveUploads = async (upload: PendingUpload) => {
    setIsSavingUpload(true);
    setNameError(null);
    const uploaded = { ...uploadedRecords };
    const saved = { ...savedUploads };
    let savingIndex = 0;

    try {
      for (const [index, doc] of effectiveDocuments.entries()) {
        savingIndex = index;
        if (doc.VaultFileId in saved) continue;
        const draft = drafts[doc.VaultFileId] ?? buildDraft(doc);
        const name = draft.name.trim();
        let record = uploaded[doc.VaultFileId];
        if (!record) {
          const response: NetworkedDocumentRecord = await uploadDocument({
            file: upload.files[index],
            folderId:
              draft.selectedFolderId === null
                ? undefined
                : String(draft.selectedFolderId),
            InvestorId: investorId,
          }).unwrap();
          record = response;
          uploaded[doc.VaultFileId] = record;
          setUploadedRecords({ ...uploaded });
        }

        if (
          name !== record.Name ||
          draft.selectedAccounts.length > 0 ||
          draft.selectedFolderId !== (record.ParentId ?? null)
        ) {
          const success = await updateDocument(
            buildUpdatePayload(record, {
              ...draft,
              permissions: record.Permissions,
            }),
          ).unwrap();
          if (!success) {
            throw new Error('API returned false');
          }
          record = { ...record, Name: name };
          uploaded[doc.VaultFileId] = record;
          setUploadedRecords({ ...uploaded });
        }

        saved[doc.VaultFileId] = true;
        setSavedUploads({ ...saved });
        showSuccessSnackbar(t('documentVault.uploadSuccess', { name }));
      }

      onClose();
    } catch (error) {
      console.error('Failed to upload document(s):', error);
      setCurrentIndex(savingIndex);
      showErrorSnackbar(
        getServerMessage(error) ?? t('documentVault.uploadFailed'),
      );
    } finally {
      setIsSavingUpload(false);
    }
  };

  const isCurrentSaved =
    currentDocument !== null && currentDocument.VaultFileId in savedUploads;

  const handleClose = () => {
    if (isSavingUpload) return;
    const keptCount = Object.keys(uploadedRecords).length;
    if (keptCount > 0) {
      showErrorSnackbar(
        t('documentVault.uploadsKeptOnCancel', { count: keptCount }),
      );
    }
    onClose();
  };

  const handleSaveChanges = async () => {
    if (!open || effectiveDocuments.length === 0) return;
    if (pendingUpload) {
      await handleSaveUploads(pendingUpload);
      return;
    }

    setHouseholdSaveError(false);
    setNameError(null);
    let savingDocId: string | null = null;
    let householdFailed = false;

    try {
      for (const doc of effectiveDocuments) {
        savingDocId = doc.VaultFileId;
        const draft = drafts[doc.VaultFileId] ?? buildDraft(doc);
        const payload = buildUpdatePayload(doc, draft);

        const success = await updateDocument(payload).unwrap();
        if (!success) {
          throw new Error('API returned false');
        }

        const editedHouseholdRows = householdEdits[doc.VaultFileId];
        const householdRecord = householdRecordsByVaultFileId.get(
          doc.VaultFileId,
        );

        // Household sharing is saved after the document's own metadata, and it
        // keeps its failures to itself: a household error must not discard the
        // name/folder/association edits, nor abort the remaining documents of a
        // bulk edit. Intentional ordering — do not fold this back into the
        // outer try (EUD-434).
        if (canShareWithHousehold && editedHouseholdRows && householdRecord) {
          try {
            await updateDocumentHouseholdPermissions({
              ...householdRecord,
              Permissions: editedHouseholdRows,
            }).unwrap();
          } catch (error) {
            console.error(
              'Failed to update household sharing for document:',
              doc.VaultFileId,
              error,
            );
            householdFailed = true;
          }
        }
      }

      if (householdFailed) {
        setHouseholdSaveError(true);
        return;
      }

      onClose();
    } catch (error) {
      console.error('Failed to update document(s):', error);
      const serverMessage = getServerMessage(error);
      if (
        serverMessage === DUPLICATE_FOLDER_SERVER_MESSAGE &&
        savingDocId !== null
      ) {
        showDuplicateNameError(savingDocId);
      } else {
        showErrorSnackbar(serverMessage ?? t('documentVault.saveError'));
      }
    }
  };

  const handleNext = () => {
    if (isLastDocument) {
      void handleSaveChanges();
      return;
    }
    setCurrentIndex((prev) =>
      Math.min(prev + 1, effectiveDocuments.length - 1),
    );
    setIsFolderSelectorOpen(false);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
    setIsFolderSelectorOpen(false);
  };

  const isLoading = isLoadingFolders || isLoadingAccounts;

  // Give the accounts dropdown a meaningful empty state instead of a bare
  // "No options": explain a load failure vs. simply having no open accounts
  // (initial loading is already covered by the form-level spinner) (EUD-416).
  const accountsNoOptionsText = isAccountsError
    ? t(
        'documents.editModal.couldNotLoadAccounts',
        "We couldn't load your accounts. Please try again.",
      )
    : t(
        'documents.editModal.noOpenAccountsToAssociate',
        'No open accounts available to associate.',
      );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        Edit {currentDocument?.IsFolder ? 'Folder' : 'Document'}:{' '}
        {currentDocument?.Name}
        {isBulkEdit
          ? ` (${String(currentIndex + 1)} of ${String(effectiveDocuments.length)})`
          : ''}
      </DialogTitle>
      <DialogContent dividers>
        {householdSaveError && (
          <Alert
            severity='error'
            sx={{ mb: 2 }}
          >
            {t(
              'documents.editModal.householdSaveError',
              "We couldn't save who this document is shared with. Please try again.",
            )}
          </Alert>
        )}
        {isLoading ? (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            minHeight='300px'
          >
            <CircularProgress />
          </Box>
        ) : currentDocument && currentDraft ? (
          <Box
            component='form'
            noValidate
            autoComplete='off'
          >
            <TextField
              required
              margin='dense'
              id='name'
              label='Name'
              type='text'
              fullWidth
              variant='outlined'
              value={currentDraft.name}
              disabled={isCurrentSaved || isSavingUpload}
              onChange={(e) => {
                setNameError(null);
                updateCurrentDraft((draft) => ({
                  ...draft,
                  name: e.target.value,
                }));
              }}
              error={currentNameError !== undefined}
              helperText={currentNameError}
              sx={{ mb: 2 }}
            />

            <Typography
              variant='subtitle1'
              gutterBottom
              sx={{ fontWeight: 'medium' }}
            >
              What folder is this in?
            </Typography>
            <Box
              display='flex'
              alignItems='center'
              sx={{ mb: 2 }}
            >
              <Typography sx={{ mr: 1 }}>
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: keep the fallback in case selectedFolder or its name is ever missing */}
                {selectedFolder?.name ?? rootLabel}
              </Typography>
              {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive optional chain on currentDocument */}
              {currentDocument?.CanMove &&
                !isCurrentSaved &&
                !isSavingUpload && (
                  <Link
                    component='button'
                    type='button'
                    variant='body2'
                    onClick={() => {
                      setIsFolderSelectorOpen(true);
                    }}
                  >
                    Change Folder
                  </Link>
                )}
            </Box>

            {/* Folder Selection Autocomplete - shown when link clicked */}
            {/* Using a separate state to control its visibility avoids layout shifts */}
            {isFolderSelectorOpen && (
              <Autocomplete
                open={isFolderSelectorOpen} // Control open state
                onClose={() => {
                  setIsFolderSelectorOpen(false);
                }} // Close when blurred
                id='folder-autocomplete'
                options={folderOptions}
                noOptionsText={t('noFoldersAvailable', 'No folders available')}
                getOptionLabel={(option) => option.name}
                getOptionKey={(option) => option.id ?? 'root'}
                value={selectedFolder}
                onChange={(_event, newValue) => {
                  setNameError(null);
                  updateCurrentDraft((draft) => ({
                    ...draft,
                    selectedFolderId: newValue?.id ?? null,
                  }));
                  setIsFolderSelectorOpen(false); // Close after selection
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Select Folder'
                    variant='outlined'
                    autoFocus
                    // autoFocus // Focus when it appears
                  />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{ paddingLeft: `${String(option.level * 1.5)}em` }}
                  >
                    {option.name.split(' / ').pop()}{' '}
                    {/* Show only last part of name in dropdown */}
                  </li>
                )}
                sx={{ mb: 2 }} // Add margin if needed when visible
              />
            )}

            {/* Associated Accounts - Using Autocomplete */}
            {!currentDocument.IsFolder && (
              <Autocomplete
                multiple
                id='associated-accounts-autocomplete'
                options={accountOptions}
                getOptionLabel={(option) => option.name}
                value={currentDraft.selectedAccounts}
                disabled={isCurrentSaved || isSavingUpload}
                getOptionKey={(option) => option.id}
                noOptionsText={accountsNoOptionsText}
                onChange={(_event, newValue) => {
                  updateCurrentDraft((draft) => ({
                    ...draft,
                    selectedAccounts: newValue,
                    accountsEdited: true,
                  }));
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    label='Associated Accounts (Optional)'
                    // Surface a load failure as neutral helper text (not a red
                    // validation error) on this optional field (EUD-416).
                    helperText={
                      isAccountsError ? accountsNoOptionsText : undefined
                    }
                    placeholder={
                      currentDraft.selectedAccounts.length === 0
                        ? 'Select accounts'
                        : ''
                    }
                  />
                )}
                sx={{ mb: 2 }}
              />
            )}

            {showFinancialTeamPermissions && (
              <>
                <Typography
                  variant='subtitle1'
                  gutterBottom
                  sx={{ fontWeight: 'medium' }}
                >
                  {t(
                    'documents.editModal.whoCanSeeThis',
                    'Who in your Financial Team can see this?',
                  )}
                </Typography>
                <Box
                  data-testid='financial-team-permissions'
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      sm: 'repeat(3, minmax(0, 1fr))',
                      md: 'repeat(4, minmax(0, 1fr))',
                    },
                    columnGap: 1,
                    rowGap: 0.5,
                    mb: 2,
                  }}
                >
                  {currentDraft.permissions.map((permission) => (
                    <FormControlLabel
                      key={permission.AdvisorId}
                      sx={{ m: 0, alignItems: 'flex-start' }}
                      control={
                        <Checkbox
                          checked={permission.AllowedToSee}
                          disabled={
                            !permission.CanEdit || isImpersonating || isUpdating
                          }
                          onChange={(e) => {
                            handlePermissionChange(
                              permission.AdvisorId,
                              e.target.checked,
                            );
                          }}
                          name={permission.AdvisorName}
                        />
                      }
                      label={
                        <Typography
                          variant='body2'
                          sx={{ overflowWrap: 'anywhere', mt: '9px' }}
                        >
                          {permission.AdvisorName}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </>
            )}

            {showHouseholdSharing && (
              <>
                <Typography
                  variant='subtitle1'
                  gutterBottom
                  sx={{ fontWeight: 'medium' }}
                >
                  {t(
                    'documents.editModal.shareDocumentWith',
                    'Share Document With',
                  )}
                </Typography>
                <Box
                  data-testid='household-permissions'
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                    },
                    columnGap: 1,
                    rowGap: 0.5,
                    mb: 2,
                  }}
                >
                  {currentHouseholdRows.map((row) => (
                    <FormControlLabel
                      key={row.InvestorHHId}
                      sx={{ m: 0, alignItems: 'flex-start' }}
                      control={
                        <Checkbox
                          checked={row.CanSee}
                          disabled={
                            isUpdating || isUpdatingHouseholdPermissions
                          }
                          onChange={(e) => {
                            handleHouseholdPermissionChange(
                              row.InvestorHHId,
                              e.target.checked,
                            );
                          }}
                          name={`household-${String(row.InvestorHHId)}`}
                        />
                      }
                      label={
                        <Typography
                          variant='body2'
                          sx={{ overflowWrap: 'anywhere', mt: '9px' }}
                        >
                          {formatHouseholdMembers(
                            row.Members,
                            householdConjunction,
                          )}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        ) : (
          <Typography>No document selected.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          size='large'
          variant='outlined'
          onClick={handleClose}
          disabled={isSavingUpload}
        >
          Cancel
        </Button>
        {isBulkEdit && (
          <Button
            size='large'
            variant='outlined'
            onClick={handlePrevious}
            disabled={
              isUpdating ||
              isUpdatingHouseholdPermissions ||
              isLoading ||
              currentIndex === 0 ||
              !currentDocument
            }
          >
            Previous
          </Button>
        )}
        <Button
          size='large'
          onClick={handleNext}
          variant='contained'
          color='primary'
          // Block Save/Next until accounts (and folders) finish loading —
          // otherwise handleSaveChanges falls back to buildDraft with an empty
          // accountOptions and would send Associations: [] (EUD-416).
          disabled={
            isUpdating ||
            isUpdatingHouseholdPermissions ||
            isLoading ||
            !currentDocument ||
            !currentDraft?.name.trim()
          }
        >
          {isSavingUpload ? (
            t('uploading')
          ) : isUpdating || isUpdatingHouseholdPermissions ? (
            <CircularProgress size={24} />
          ) : isLastDocument ? (
            'Save'
          ) : (
            'Next'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDocumentModal;
